import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useSchool } from '../context/SchoolContext';
import {
  CloudSun, Thermometer, Building2, ArrowRight,
  Plus, Trash2, Camera, Upload, X, Image as ImageIcon,
  Clock, MapPin, CalendarDays, Loader2, AlertTriangle,
  Eye, BookOpen, Search, ChevronDown, ChevronUp, Bell,
  CalendarClock, Radio, Sparkles, LayoutDashboard
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const DUTY_SLOTS = ['Before School', 'Recess', 'Lunch', 'After School', 'Other'];

// Briefing card for "Last Time You Taught"
const BriefingCard = ({ classId, className, classCode, briefing, isLoading, isExpanded, onToggle }) => {
  if (isLoading) {
    return (
      <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-3">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        <span className="text-sm text-slate-400">Loading {className}...</span>
      </div>
    );
  }

  if (!briefing) return null;

  const att = briefing.last_attendance;
  const beh = briefing.behaviour;
  const hasWatchStudents = briefing.watch_list?.length > 0;
  const hasConcerns = beh?.concern_students?.length > 0;

  return (
    <div
      className="rounded-xl border border-slate-200 overflow-hidden transition-all"
      data-testid={`briefing-${classId}`}
    >
      {/* Header - always visible */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors text-left"
        data-testid={`briefing-toggle-${classId}`}
      >
        <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-4 h-4 text-violet-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800">{className}</p>
          <p className="text-xs text-slate-500">
            {briefing.student_count} students
            {att?.date && <span className="ml-1">&middot; Last: {att.date}</span>}
            {hasWatchStudents && <span className="ml-1 text-amber-600">&middot; {briefing.watch_list.length} on watch</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {beh?.negative > 0 && (
            <span className="text-xs bg-rose-50 text-rose-600 px-1.5 py-0.5 rounded-md font-medium">
              {beh.negative} issues
            </span>
          )}
          {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {/* Expanded details */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-slate-100 pt-3 animate-fade-in">
          {/* Latest classwork from last session */}
          {briefing.classwork?.length > 0 && (
            <div className="bg-violet-50/50 rounded-lg p-2.5 border border-violet-100">
              <p className="text-[10px] uppercase tracking-wider text-violet-500 font-semibold mb-1.5">
                What they were working on{briefing.last_session_date ? ` (${briefing.last_session_date})` : ''}
              </p>
              <div className="space-y-1">
                {briefing.classwork.map((cw, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      cw.status === 'completed' ? 'bg-emerald-500' : cw.status === 'in_progress' ? 'bg-amber-500' : 'bg-slate-300'
                    }`} />
                    <span className="text-slate-700">{cw.title}</span>
                    <span className="text-slate-400 text-[10px]">{cw.status === 'completed' ? 'Done' : cw.status === 'in_progress' ? 'In progress' : 'Not started'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Last attendance */}
          {att?.date && (
            <div className="flex items-center gap-4 text-xs">
              <span className="text-slate-500">Last roll ({att.date}):</span>
              <span className="text-emerald-600 font-medium">{att.present} present</span>
              {att.late > 0 && <span className="text-amber-600 font-medium">{att.late} late</span>}
              {att.absent > 0 && <span className="text-rose-500 font-medium">{att.absent} absent</span>}
            </div>
          )}
          {att?.absent_names?.length > 0 && (
            <p className="text-xs text-slate-500">
              Absent last time: <span className="text-slate-700">{att.absent_names.join(', ')}</span>
            </p>
          )}

          {/* Behaviour summary */}
          {(beh?.positive > 0 || beh?.negative > 0) && (
            <div className="flex items-center gap-4 text-xs">
              <span className="text-slate-500">Recent behaviour (14d):</span>
              <span className="text-emerald-600 font-medium">+{beh.positive}</span>
              <span className="text-rose-500 font-medium">-{beh.negative}</span>
            </div>
          )}

          {/* Who to look out for */}
          {(hasConcerns || hasWatchStudents) && (
            <div className="bg-amber-50/50 rounded-lg p-2.5 border border-amber-100">
              <p className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold mb-1.5 flex items-center gap-1">
                <Eye className="w-3 h-3" /> Who to look out for
              </p>
              <div className="space-y-1.5">
                {hasConcerns && beh.concern_students.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <AlertTriangle className="w-3 h-3 text-rose-400 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{s.name}</span>
                    <span className="text-slate-400">({s.negative_count} negative logs recently)</span>
                  </div>
                ))}
                {briefing.watch_list?.map((w, i) => (
                  <div key={`w-${i}`} className="flex items-start gap-2 text-xs">
                    <Eye className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-slate-700 font-medium">{w.name}</span>
                      {w.flags?.length > 0 && (
                        <span className="text-slate-400 ml-1">- {w.flags.join(', ')}</span>
                      )}
                      {w.notes && <p className="text-slate-400 mt-0.5">{w.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent incidents */}
          {beh?.recent_incidents?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Recent incidents</p>
              <div className="space-y-1">
                {beh.recent_incidents.map((inc, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0" />
                    <span className="font-medium">{inc.student_name}</span>
                    <span className="text-slate-400 truncate">{inc.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Link to class */}
          <Link
            to={`/students/${classId}`}
            className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 pt-1"
          >
            Go to class <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}
    </div>
  );
};

export const DailyOverviewPage = () => {
  const { user } = useAuth();
  const { selectedSchool } = useSchool();
  const [schools, setSchools] = useState([]);
  const [overview, setOverview] = useState(null);
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [duties, setDuties] = useState([]);
  const [dutyDialogOpen, setDutyDialogOpen] = useState(false);
  const [newDuty, setNewDuty] = useState({ time_slot: 'Recess', location: '', notes: '' });
  const [scheduleMode, setScheduleMode] = useState('timetable'); // 'timetable' | 'photo'
  const [schedulePhoto, setSchedulePhoto] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [briefings, setBriefings] = useState({});
  const [briefingLoading, setBriefingLoading] = useState({});
  const [expandedBriefing, setExpandedBriefing] = useState(null);
  const [allClasses, setAllClasses] = useState([]);
  const [classSearch, setClassSearch] = useState('');
  const [manualClassId, setManualClassId] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [bellTimes, setBellTimes] = useState([]);
  const [specialDay, setSpecialDay] = useState(false);
  const [specialBellOpen, setSpecialBellOpen] = useState(false);
  const [customBells, setCustomBells] = useState([]);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Live clock - update every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const today = new Date();
  const hour = today.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.name?.split(' ')[0] || 'Teacher';

  // Fetch all data
  useEffect(() => {
    if (!selectedSchool) return;
    const fetchAll = async () => {
      try {
        const todayStr = format(new Date(), 'yyyy-MM-dd');
        const [schoolsRes, overviewRes, dutiesRes, photoRes, classesRes, bellsRes] = await Promise.all([
          fetch(`${API}/schools`, { credentials: 'include' }),
          fetch(`${API}/daily-overview/${selectedSchool.school_id}`, { credentials: 'include' }),
          fetch(`${API}/duties/${selectedSchool.school_id}?date=${todayStr}`, { credentials: 'include' }),
          fetch(`${API}/schedule-photo/${selectedSchool.school_id}`, { credentials: 'include' }),
          fetch(`${API}/classes?school_id=${selectedSchool.school_id}`, { credentials: 'include' }),
          fetch(`${API}/bell-times/${selectedSchool.school_id}`, { credentials: 'include' }),
        ]);
        if (schoolsRes.ok) setSchools(await schoolsRes.json());
        let overviewData = null;
        if (overviewRes.ok) {
          overviewData = await overviewRes.json();
          setOverview(overviewData);
        }
        if (dutiesRes.ok) setDuties(await dutiesRes.json());
        if (photoRes.ok) {
          const photoData = await photoRes.json();
          if (photoData.image_data) {
            setSchedulePhoto(photoData.image_data);
            setScheduleMode('photo');
          }
        }
        if (classesRes.ok) {
          const classes = await classesRes.json();
          setAllClasses(classes.filter(c => !c.archived));
        }
        if (bellsRes.ok) setBellTimes(await bellsRes.json());

        // Fetch briefings for scheduled classes
        if (overviewData?.schedule?.length > 0) {
          const scheduledClassIds = [...new Set(
            overviewData.schedule.filter(s => s.class_id).map(s => s.class_id)
          )];
          fetchBriefings(scheduledClassIds);
        }
      } catch (error) {
        console.error('Failed to fetch:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [selectedSchool]);

  // Fetch weather using geolocation + Open-Meteo
  useEffect(() => {
    const fetchWeather = async (lat, lon) => {
      try {
        const r = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=temperature_2m_max,temperature_2m_min,weathercode&timezone=auto&forecast_days=1`
        );
        if (r.ok) {
          const data = await r.json();
          setWeather({
            min: Math.round(data.daily.temperature_2m_min[0]),
            max: Math.round(data.daily.temperature_2m_max[0]),
            code: data.daily.weathercode[0],
          });
        }
      } catch (error) {
        console.error('Weather fetch failed:', error);
      } finally {
        setWeatherLoading(false);
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => fetchWeather(pos.coords.latitude, pos.coords.longitude),
        () => {
          // Fallback: Sydney coordinates
          fetchWeather(-33.87, 151.21);
        },
        { timeout: 5000 }
      );
    } else {
      fetchWeather(-33.87, 151.21);
    }
  }, []);

  const getWeatherLabel = (code) => {
    if (code <= 1) return 'Clear';
    if (code <= 3) return 'Partly cloudy';
    if (code <= 48) return 'Overcast';
    if (code <= 67) return 'Rain';
    if (code <= 77) return 'Snow';
    if (code <= 82) return 'Showers';
    return 'Stormy';
  };

  // Fetch briefings for multiple classes
  const fetchBriefings = async (classIds) => {
    const loadingState = {};
    classIds.forEach(id => { loadingState[id] = true; });
    setBriefingLoading(prev => ({ ...prev, ...loadingState }));

    const results = await Promise.all(
      classIds.map(async (classId) => {
        try {
          const r = await fetch(`${API}/class-briefing/${classId}`, { credentials: 'include' });
          if (r.ok) return { classId, data: await r.json() };
        } catch (e) { /* skip */ }
        return { classId, data: null };
      })
    );

    const newBriefings = {};
    const doneLoading = {};
    results.forEach(({ classId, data }) => {
      if (data) newBriefings[classId] = data;
      doneLoading[classId] = false;
    });
    setBriefings(prev => ({ ...prev, ...newBriefings }));
    setBriefingLoading(prev => ({ ...prev, ...doneLoading }));
  };

  // Fetch single briefing (for manual search)
  const fetchSingleBriefing = async (classId) => {
    setBriefingLoading(prev => ({ ...prev, [classId]: true }));
    try {
      const r = await fetch(`${API}/class-briefing/${classId}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setBriefings(prev => ({ ...prev, [classId]: data }));
        setExpandedBriefing(classId);
        setManualClassId(classId);
      }
    } catch (e) {
      toast.error('Failed to load class briefing');
    } finally {
      setBriefingLoading(prev => ({ ...prev, [classId]: false }));
    }
  };

  // Filter classes for search
  const filteredClasses = classSearch.trim()
    ? allClasses.filter(c =>
        c.name?.toLowerCase().includes(classSearch.toLowerCase()) ||
        c.class_code?.toLowerCase().includes(classSearch.toLowerCase())
      )
    : [];

  // Duties
  const handleAddDuty = async () => {
    if (!newDuty.time_slot) return;
    try {
      const r = await fetch(`${API}/duties/${selectedSchool.school_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newDuty),
      });
      if (r.ok) {
        const created = await r.json();
        setDuties(prev => [...prev, created]);
        setDutyDialogOpen(false);
        setNewDuty({ time_slot: 'Recess', location: '', notes: '' });
        toast.success('Duty added');
      }
    } catch (error) {
      toast.error('Failed to add duty');
    }
  };

  const handleDeleteDuty = async (dutyId) => {
    try {
      const r = await fetch(`${API}/duties/${dutyId}`, { method: 'DELETE', credentials: 'include' });
      if (r.ok) {
        setDuties(prev => prev.filter(d => d.duty_id !== dutyId));
        toast.success('Duty removed');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Schedule photo
  const handlePhotoUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setPhotoUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageData = e.target.result;
        const r = await fetch(`${API}/schedule-photo/${selectedSchool.school_id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ image_data: imageData }),
        });
        if (r.ok) {
          setSchedulePhoto(imageData);
          setScheduleMode('photo');
          toast.success('Schedule photo uploaded');
        } else {
          toast.error('Upload failed');
        }
        setPhotoUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Upload failed');
      setPhotoUploading(false);
    }
  };

  const handleDeletePhoto = async () => {
    try {
      await fetch(`${API}/schedule-photo/${selectedSchool.school_id}`, { method: 'DELETE', credentials: 'include' });
      setSchedulePhoto(null);
      setScheduleMode('timetable');
      toast.success('Photo removed');
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const d = overview || {};
  const breadcrumb = [{ label: 'Today' }];

  // Calculate next bell
  const getNextBell = () => {
    const activeBells = specialDay && customBells.length > 0 ? customBells.map(b => ({ name: b.label, time: b.time })) : bellTimes;
    if (activeBells.length === 0) return null;
    const now = currentTime;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    
    const upcoming = activeBells
      .map(b => {
        const [h, m] = b.time.split(':').map(Number);
        const bellMinutes = h * 60 + m;
        return { ...b, totalMinutes: bellMinutes, diff: bellMinutes - nowMinutes };
      })
      .filter(b => b.diff > 0)
      .sort((a, b) => a.diff - b.diff);
    
    return upcoming.length > 0 ? upcoming[0] : null;
  };

  const nextBell = getNextBell();
  const timeStr = format(currentTime, 'h:mm:ss a');

  if (loading) {
    return (
      <Layout breadcrumb={breadcrumb}>
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your day...
        </div>
      </Layout>
    );
  }

  return (
    <Layout breadcrumb={breadcrumb}>
      <div className="space-y-6 max-w-3xl" data-testid="daily-overview-page">

        {/* 1. Greeting + Weather */}
        <div className="animate-fade-in">
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900 tracking-tight" data-testid="greeting">
            {greeting}, {firstName}
          </h1>
          <p className="text-slate-500 mt-1 text-sm flex items-center gap-2 flex-wrap">
            <span>{format(today, 'EEEE, MMMM d, yyyy')}</span>
            <span className="text-slate-300">|</span>
            <span className="font-mono font-medium text-slate-700" data-testid="live-clock">{timeStr}</span>
            {nextBell && (
              <>
                <span className="text-slate-300">|</span>
                <span className="text-red-500 font-semibold flex items-center gap-1" data-testid="bell-countdown">
                  <Bell className="w-3.5 h-3.5" />
                  {nextBell.diff} min until {nextBell.name || 'next bell'}
                </span>
              </>
            )}
          </p>

          {/* Weather */}
          <div className="mt-4 flex items-center gap-3" data-testid="weather-widget">
            <div className="w-10 h-10 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center">
              <CloudSun className="w-5 h-5 text-sky-500" />
            </div>
            {weatherLoading ? (
              <span className="text-sm text-slate-400">Fetching weather...</span>
            ) : weather ? (
              <div>
                <p className="text-sm font-medium text-slate-700">
                  {getWeatherLabel(weather.code)}
                </p>
                <p className="text-xs text-slate-500 flex items-center gap-2">
                  <Thermometer className="w-3 h-3" />
                  <span>Low <span className="font-semibold text-blue-600">{weather.min}&deg;C</span></span>
                  <span className="text-slate-300">|</span>
                  <span>High <span className="font-semibold text-orange-500">{weather.max}&deg;C</span></span>
                </p>
              </div>
            ) : (
              <span className="text-sm text-slate-400">Weather unavailable</span>
            )}
          </div>
        </div>

        {/* Quick Actions — Core teacher tools */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 animate-fade-in" style={{ animationDelay: '50ms' }} data-testid="quick-actions">
          <Link to="/app/live-mode" className="group" data-testid="action-teach-now">
            <Card className="h-full border-2 border-rose-100 bg-gradient-to-br from-rose-50 to-white hover:border-rose-300 hover:shadow-md transition-all duration-200 overflow-hidden">
              <CardContent className="p-5 flex flex-col items-start gap-3">
                <div className="w-12 h-12 rounded-2xl bg-rose-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Radio className="w-6 h-6 text-rose-600" />
                </div>
                <div>
                  <h3 className="text-base font-heading font-semibold text-slate-900">Teach Now</h3>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Start a live lesson with attendance, behaviour tracking & timers</p>
                </div>
                <span className="text-xs font-medium text-rose-600 flex items-center gap-1 mt-auto group-hover:gap-2 transition-all">
                  Start teaching <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </CardContent>
            </Card>
          </Link>

          <Link to="/app/dashboard" className="group" data-testid="action-classes">
            <Card className="h-full border-2 border-teal-100 bg-gradient-to-br from-teal-50 to-white hover:border-teal-300 hover:shadow-md transition-all duration-200 overflow-hidden">
              <CardContent className="p-5 flex flex-col items-start gap-3">
                <div className="w-12 h-12 rounded-2xl bg-teal-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <LayoutDashboard className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-base font-heading font-semibold text-slate-900">Classes</h3>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">View class lists, student info & seating plans</p>
                </div>
                <span className="text-xs font-medium text-teal-600 flex items-center gap-1 mt-auto group-hover:gap-2 transition-all">
                  View classes <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </CardContent>
            </Card>
          </Link>

          <Link to="/app/summaries" className="group" data-testid="action-summaries">
            <Card className="h-full border-2 border-violet-100 bg-gradient-to-br from-violet-50 to-white hover:border-violet-300 hover:shadow-md transition-all duration-200 overflow-hidden">
              <CardContent className="p-5 flex flex-col items-start gap-3">
                <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Sparkles className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <h3 className="text-base font-heading font-semibold text-slate-900">Lesson Summaries</h3>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Write end-of-lesson notes & AI-powered handovers</p>
                </div>
                <span className="text-xs font-medium text-violet-600 flex items-center gap-1 mt-auto group-hover:gap-2 transition-all">
                  Write summary <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* 2. Today's Schedule */}
        <Card className="bg-white border border-slate-200 shadow-soft animate-fade-in" style={{ animationDelay: '100ms' }} data-testid="schedule-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-heading font-semibold text-slate-800 flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-teal-500" /> Today's Schedule
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSpecialBellOpen(true)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                    specialDay
                      ? 'bg-violet-100 text-violet-700 border border-violet-300'
                      : 'bg-slate-100 text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="today-special-day-btn"
                >
                  <CalendarClock className="w-3.5 h-3.5" />
                  {specialDay ? 'Special Day' : 'Special Day'}
                </button>
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="schedule-mode-toggle">
                <button
                  onClick={() => setScheduleMode('timetable')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    scheduleMode === 'timetable' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="mode-timetable-btn"
                >
                  Build
                </button>
                <button
                  onClick={() => setScheduleMode('photo')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    scheduleMode === 'photo' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="mode-photo-btn"
                >
                  Photo
                </button>
              </div>
              </div>
            </div>

            {scheduleMode === 'timetable' ? (
              /* Timetable view */
              <div>
                {(!d.schedule || d.schedule.length === 0) && (!d.periods_per_day || d.periods_per_day === 0) ? (
                  <div className="py-6 text-center">
                    <CalendarDays className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">No schedule set up yet</p>
                    <Link to="/app/timetable" className="text-xs text-teal-600 hover:text-teal-700 mt-1 inline-block">
                      Set up timetable
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {Array.from({ length: d.periods_per_day || 6 }, (_, i) => {
                      const entry = d.schedule?.find(s => s.period === i + 1);
                      return (
                        <div
                          key={i}
                          className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                            entry?.class_name
                              ? 'bg-teal-50/60 border border-teal-100'
                              : 'bg-slate-50 border border-transparent'
                          }`}
                          data-testid={`period-${i + 1}`}
                        >
                          <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-slate-500">P{i + 1}</span>
                          </div>
                          {entry?.class_name ? (
                            <Link to={`/students/${entry.class_id}`} className="flex-1 min-w-0 group">
                              <p className="text-sm font-medium text-slate-800 group-hover:text-teal-600 transition-colors">
                                {entry.class_name}
                              </p>
                              {entry.class_code && <p className="text-xs text-slate-500">{entry.class_code}</p>}
                            </Link>
                          ) : (
                            <span className="text-sm text-slate-400">Free period</span>
                          )}
                        </div>
                      );
                    })}
                    <div className="pt-2">
                      <Link to="/app/timetable" className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
                        Edit timetable <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Photo view */
              <div>
                {schedulePhoto ? (
                  <div className="relative">
                    <img
                      src={schedulePhoto}
                      alt="Schedule"
                      className="w-full rounded-xl border border-slate-200"
                      data-testid="schedule-photo-img"
                    />
                    <button
                      onClick={handleDeletePhoto}
                      className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/50 text-white hover:bg-black/70 transition-colors"
                      data-testid="delete-photo-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="py-8 text-center border-2 border-dashed border-slate-200 rounded-xl">
                    <ImageIcon className="w-8 h-8 text-slate-200 mx-auto mb-3" />
                    <p className="text-sm text-slate-400 mb-4">Upload or take a photo of your schedule</p>
                    <div className="flex items-center justify-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={photoUploading}
                        className="border-slate-200 text-slate-600 rounded-xl text-xs"
                        data-testid="upload-photo-btn"
                      >
                        {photoUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                        Upload
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => cameraInputRef.current?.click()}
                        disabled={photoUploading}
                        className="border-slate-200 text-slate-600 rounded-xl text-xs"
                        data-testid="take-photo-btn"
                      >
                        <Camera className="w-3.5 h-3.5 mr-1.5" /> Take Photo
                      </Button>
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handlePhotoUpload(e.target.files[0])}
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handlePhotoUpload(e.target.files[0])}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* 4. Duties */}
        <Card className="bg-white border border-slate-200 shadow-soft animate-fade-in" style={{ animationDelay: '150ms' }} data-testid="duties-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-heading font-semibold text-slate-800 flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" /> Duties
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDutyDialogOpen(true)}
                className="border-slate-200 text-slate-600 rounded-xl text-xs h-7"
                data-testid="add-duty-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>

            {duties.length === 0 ? (
              <div className="py-6 text-center">
                <Clock className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No duties assigned today</p>
                <p className="text-xs text-slate-400 mt-0.5">Tap "Add" to log your duties</p>
              </div>
            ) : (
              <div className="space-y-2">
                {duties.map((duty) => (
                  <div
                    key={duty.duty_id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-amber-50/50 border border-amber-100"
                    data-testid={`duty-${duty.duty_id}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{duty.time_slot}</p>
                      {duty.location && (
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {duty.location}
                        </p>
                      )}
                      {duty.notes && <p className="text-xs text-slate-400 mt-0.5">{duty.notes}</p>}
                    </div>
                    <button
                      onClick={() => handleDeleteDuty(duty.duty_id)}
                      className="text-slate-300 hover:text-rose-500 transition-colors p-1"
                      data-testid={`delete-duty-${duty.duty_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 5. Last Time You Taught This Class */}
        <Card className="bg-white border border-slate-200 shadow-soft animate-fade-in" style={{ animationDelay: '200ms' }} data-testid="briefing-card">
          <CardContent className="p-5">
            <h2 className="text-sm font-heading font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-violet-500" /> Last Time You Taught
            </h2>

            {/* Briefing cards from timetable */}
            {d.schedule?.filter(s => s.class_id).length > 0 ? (
              <div className="space-y-2 mb-4">
                {[...new Map(d.schedule.filter(s => s.class_id).map(s => [s.class_id, s])).values()].map((entry) => {
                  const b = briefings[entry.class_id];
                  const isLoading = briefingLoading[entry.class_id];
                  const isExpanded = expandedBriefing === entry.class_id;
                  return (
                    <BriefingCard
                      key={entry.class_id}
                      classId={entry.class_id}
                      className={entry.class_name}
                      classCode={entry.class_code}
                      briefing={b}
                      isLoading={isLoading}
                      isExpanded={isExpanded}
                      onToggle={() => setExpandedBriefing(isExpanded ? null : entry.class_id)}
                    />
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-400 mb-4">No classes in today's timetable</p>
            )}

            {/* Manual class search */}
            <div className="border-t border-slate-100 pt-3" data-testid="class-search">
              <p className="text-xs font-medium text-slate-500 mb-2">Search for a class</p>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Type class name or code..."
                  value={classSearch}
                  onChange={(e) => setClassSearch(e.target.value)}
                  className="pl-9 border-slate-200 rounded-xl text-sm h-9"
                  data-testid="class-search-input"
                />
              </div>
              {filteredClasses.length > 0 && (
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {filteredClasses.map((cls) => (
                    <button
                      key={cls.class_id}
                      onClick={() => { fetchSingleBriefing(cls.class_id); setClassSearch(''); }}
                      className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-teal-50 transition-colors flex items-center justify-between"
                      data-testid={`search-result-${cls.class_id}`}
                    >
                      <span>{cls.name}</span>
                      {cls.class_code && <span className="text-xs text-slate-400">{cls.class_code}</span>}
                    </button>
                  ))}
                </div>
              )}
              {classSearch.trim() && filteredClasses.length === 0 && (
                <p className="text-xs text-slate-400 mt-2 text-center py-2">No classes found</p>
              )}

              {/* Show manually selected briefing */}
              {manualClassId && briefings[manualClassId] && !d.schedule?.some(s => s.class_id === manualClassId) && (
                <div className="mt-3">
                  <BriefingCard
                    classId={manualClassId}
                    className={briefings[manualClassId].class_name}
                    classCode={briefings[manualClassId].class_code}
                    briefing={briefings[manualClassId]}
                    isLoading={briefingLoading[manualClassId]}
                    isExpanded={expandedBriefing === manualClassId}
                    onToggle={() => setExpandedBriefing(expandedBriefing === manualClassId ? null : manualClassId)}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Add Duty Dialog */}
        <Dialog open={dutyDialogOpen} onOpenChange={setDutyDialogOpen}>
          <DialogContent className="bg-white max-w-sm rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg text-slate-900">Add Duty</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-1.5">Time Slot</label>
                <div className="flex flex-wrap gap-2">
                  {DUTY_SLOTS.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => setNewDuty(prev => ({ ...prev, time_slot: slot }))}
                      className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                        newDuty.time_slot === slot
                          ? 'bg-amber-100 border-amber-300 text-amber-800'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                      data-testid={`duty-slot-${slot.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-1.5">Location</label>
                <Input
                  placeholder="e.g. Front gate, Playground..."
                  value={newDuty.location}
                  onChange={(e) => setNewDuty(prev => ({ ...prev, location: e.target.value }))}
                  className="border-slate-200 rounded-xl text-sm"
                  data-testid="duty-location-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-1.5">Notes (optional)</label>
                <Input
                  placeholder="Any additional notes..."
                  value={newDuty.notes}
                  onChange={(e) => setNewDuty(prev => ({ ...prev, notes: e.target.value }))}
                  className="border-slate-200 rounded-xl text-sm"
                  data-testid="duty-notes-input"
                />
              </div>
              <Button
                onClick={handleAddDuty}
                disabled={!newDuty.time_slot}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
                data-testid="save-duty-btn"
              >
                Add Duty
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Special Day Bell Times Dialog */}
      <Dialog open={specialBellOpen} onOpenChange={setSpecialBellOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-slate-900 flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-violet-600" />Special Day Bell Times
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-500">
              Set custom bell times for today only. This won't change your saved timetable.
            </p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {customBells.map((bell, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={bell.label}
                    onChange={(e) => {
                      setCustomBells(prev => prev.map((b, i) => i === idx ? { ...b, label: e.target.value } : b));
                    }}
                    className="flex-1 border-slate-200 text-sm"
                    placeholder="Period label"
                    data-testid={`today-custom-bell-label-${idx}`}
                  />
                  <Input
                    type="time"
                    value={bell.time}
                    onChange={(e) => {
                      setCustomBells(prev => prev.map((b, i) => i === idx ? { ...b, time: e.target.value } : b));
                    }}
                    className="w-32 border-slate-200 text-sm"
                    data-testid={`today-custom-bell-time-${idx}`}
                  />
                  <button
                    onClick={() => setCustomBells(prev => prev.filter((_, i) => i !== idx))}
                    className="w-8 h-8 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-colors text-lg"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={() => setCustomBells(prev => [...prev, { label: `Period ${prev.length + 1}`, time: '09:00' }])} className="w-full border-dashed border-slate-300 text-slate-500" data-testid="today-add-bell-btn">
              + Add Bell Time
            </Button>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (customBells.length > 0) {
                    setSpecialDay(true);
                    toast.success('Special day bell times set for today');
                  } else {
                    setSpecialDay(false);
                  }
                  setSpecialBellOpen(false);
                }}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
                data-testid="today-apply-special-day-btn"
              >
                {customBells.length > 0 ? 'Apply' : 'Close'}
              </Button>
              {specialDay && (
                <Button
                  variant="outline"
                  onClick={() => { setSpecialDay(false); setCustomBells([]); setSpecialBellOpen(false); toast.success('Reverted to normal bell times'); }}
                  className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
                  data-testid="today-revert-bells-btn"
                >
                  Revert to Normal
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
